<script setup lang="ts">
import { FormKit } from '@formkit/vue';
import type { AccountFormsProfileUpdateProps } from '~/components/AccountForms/types';
import { useSdk } from '~/sdk';
import useNotifications from '~/stores/notifications';
import PasswordStrengthIndicator from '~/components/PasswordStrengthIndicator.vue';

const props = defineProps<AccountFormsProfileUpdateProps>();
const { firstName, lastName, id_gender, email } = toRefs(props);

const authStore = useAuth();
const { send } = useNotifications();
const config = inject(Symbol.for('FormKitConfig')) as any;
const { locale, t } = useI18n();
config.locale = locale.value;

const userData = ref({
  firstName: firstName?.value ?? '',
  lastName: lastName?.value ?? '',
  gender: String(id_gender?.value ?? ''),
  email: email?.value ?? '',
  password: '',
});
const errors = ref<any>([]);

const selectOptions = computed(() => [
  { value: '1', label: 'male' },
  { value: '2', label: 'female' },
]);

const onSubmit = async () => {
  errors.value = [];

  const res = await useSdk().commerce.updateCustomer({
    customer: {
      firstName: userData.value?.firstName ?? '',
      lastName: userData.value?.lastName ?? '',
      gender: userData.value?.gender ?? '',
      email: userData.value?.email ?? '',
      password: userData.value?.password ?? '',
    },
  });

  if (res.code !== 200) {
    errors.value = [...errors.value, ...[res.message]];
    return;
  }

  send({
    message: t('Success update customer'),
    type: 'success',
  });

  await authStore.refresh();
};
</script>

<template>
  <FormKit type="form" @submit="onSubmit" :submit-label="$t('Update personal data')" class="space-y-4" :errors="errors">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
      <FormKit name="firstname" v-model="userData.firstName" :label="$t('First name')" type="text" validation="required" />
      <FormKit name="lastname" v-model="userData.lastName" :label="$t('Last name')" type="text" validation="required" />
      <FormKit
        type="select"
        :label="$t('Gender')"
        v-model="userData.gender"
        name="gender"
        :options="selectOptions"
        validation="required"
      />
      <FormKit name="email" v-model="userData.email" :label="$t('Email')" type="text" validation="required" />
      <div class="w-full">
        <FormKit
          name="password"
          v-model="userData.password"
          :label="$t('Password')"
          autocomplete="current-password"
          type="password"
          validation="required|strongPassword"
        />
        <PasswordStrengthIndicator 
          :password="userData.password"
          v-if="userData.password"
          class="w-full"
        />
      </div>
    </div>
  </FormKit>
</template>
